import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Navigate } from 'react-router-dom';
import { Box, Typography, Alert } from '@mui/material';
import { useAuth } from '../../contexts/AuthContext';
import { useUserPages } from '../../hooks/useUserPages';
import { CheckingGateSpinner } from './CheckingGateSpinner';

interface PageAccessControlProps {
    children: React.ReactNode;
    requiredPage?: string; // Page name that user must have access to
    fallbackPath?: string; // Path to redirect to if no access
}

const PageAccessControl: React.FC<PageAccessControlProps> = ({
    children,
    requiredPage,
    fallbackPath = '/dashboard'
}) => {
    const { user, isAuthenticated } = useAuth();
    const { pages, loading, error } = useUserPages();

    const [hasAccess, setHasAccess] = useState<boolean | null>(null);
    const lastResultRef = useRef<boolean | null>(null);
    const lastPagesKeyRef = useRef<string>('');

    // Create stable values for comparison
    const userId = user?.id || null;
    const userRoleName = user?.role?.name || null;
    const errorMessage = error?.message || null;
    const pagesKey = useMemo(() => {
        return JSON.stringify(pages.map(p => p.name).sort());
    }, [pages]);

    useEffect(() => {
        let newAccess: boolean | null = null;

        if (!isAuthenticated || !userId) {
            newAccess = false;
        } else if (loading) {
            newAccess = null;
        } else if (errorMessage) {
            console.error('Error loading user pages:', errorMessage);
            newAccess = false;
        } else if (!requiredPage) {
            // If no specific page required, check if user has any pages
            newAccess = pages.length > 0;
        } else if (requiredPage === 'dashboard' && userRoleName === 'viewer') {
            // Additional safety: If user is a viewer and trying to access dashboard, deny access
            newAccess = false;
        } else {
            // Check if user has access to the required page
            // Normalize both the required page name and database page names for comparison
            const normalizedRequiredPage = requiredPage.toLowerCase().replace(/\s+/g, '-').replace(/_/g, '-');
            const pageNames = pages.map(p => p.name);
            
            // Check for exact match first, then normalized match
            newAccess = pages.some(page => {
                const normalizedPageName = page.name.toLowerCase().replace(/\s+/g, '-').replace(/_/g, '-');
                return page.name === requiredPage || normalizedPageName === normalizedRequiredPage ||
                       (normalizedRequiredPage === 'dri-insights' && 
                        (normalizedPageName === 'dri-insights' || 
                         (normalizedPageName.includes('dri') && normalizedPageName.includes('insight')))) ||
                       (normalizedRequiredPage === 'customer-sentiment' && 
                        (normalizedPageName === 'customer-sentiment' || 
                         normalizedPageName === 'customer-feedback-analytics' ||
                         normalizedPageName.startsWith('customer-feedback') ||
                         normalizedPageName.startsWith('customer-sentiment') ||
                         (normalizedPageName.includes('customer') && (normalizedPageName.includes('sentiment') || 
                         (normalizedPageName.includes('feedback') && normalizedPageName.includes('analytics')))))) ||
                         (normalizedRequiredPage === 'internal-project-request' && 
                          (normalizedPageName === 'internal-project-request' || 
                           normalizedPageName === 'project-request' ||
                           normalizedPageName === 'internal-project-requests' ||
                           normalizedPageName.startsWith('internal-project') ||
                           (normalizedPageName.includes('project') && normalizedPageName.includes('request')))) ||
                       (normalizedRequiredPage === 'projects-portfolio' && normalizedPageName === 'projects');
            });
            
            // Debug logging for dri-insights page access
            if (requiredPage === 'dri-insights' || normalizedRequiredPage === 'dri-insights') {
                // console.log('DRI Insights Access Check:', {
                //     requiredPage,
                //     normalizedRequiredPage,
                //     userPages: pageNames,
                //     normalizedUserPages: pages.map(p => p.name.toLowerCase().replace(/\s+/g, '-').replace(/_/g, '-')),
                //     hasAccess: newAccess,
                //     pagesCount: pages.length
                // });
            }
        }

        // Only update state if the value actually changed
        // Also check if pages actually changed (using pagesKey)
        const pagesChanged = pagesKey !== lastPagesKeyRef.current;
        const resultChanged = newAccess !== lastResultRef.current;
        
        if (resultChanged || pagesChanged) {
            lastResultRef.current = newAccess;
            lastPagesKeyRef.current = pagesKey;
            setHasAccess(newAccess);
        }
    }, [isAuthenticated, userId, userRoleName, loading, errorMessage, requiredPage, pagesKey, pages]);

    // Show loading state (main column only — sidebar is already from DashboardLayout)
    if (hasAccess === null) {
        return <CheckingGateSpinner message="Checking access permissions…" layout="embedded" />;
    }

    // Show error state
    if (error) {
        return (
            <Box
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                minHeight="100vh"
                gap={2}
                p={3}
            >
                <Alert severity="error" sx={{ maxWidth: 500 }}>
                    <Typography variant="h6" gutterBottom>
                        Access Error
                    </Typography>
                    <Typography variant="body2">
                        Unable to verify your access permissions. Please try refreshing the page or contact support.
                    </Typography>
                </Alert>
            </Box>
        );
    }

    // Redirect if no access
    if (!hasAccess) {
        return <Navigate to={fallbackPath} replace />;
    }

    // Render children if user has access
    return <>{children}</>;
};

export default PageAccessControl;
